import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogOverlay,
  AlertDialogPortal,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { LoadingIndicator } from "@/components/ui/loading-indicator";
import { NotificationRow } from "@/components/ui/notification-row";
import useMusioApi from "@/contexts/client/musio-client-context";
import { cn, formatCurrency } from "@/utils/utils";
import { useEffect, useMemo, useState, type ReactNode } from "react";

type PlanTarget = "annual" | "monthly";

type PlanChangeDialogProps = {
  target: PlanTarget;
  triggerLabel?: string;
  title?: string;
  showProrate?: boolean;
  confirmMessage?: ReactNode;
  disableOnError?: boolean;
  className?: string;
};

export default function PlanChangeDialog({
  target,
  triggerLabel,
  title,
  showProrate = false,
  confirmMessage,
  disableOnError = true,
  className,
}: PlanChangeDialogProps) {
  const { musioApi } = useMusioApi();
  const [refresh, setRefresh] = useState<boolean>(false);

  const {
    mutate: getProrate,
    data: prorateData,
    isError: isFetchError,
    isPending: isProratePending,
  } = musioApi.useMutation("post", "/user/recurring/prorate");

  const {
    mutate: upgradeMutation,
    isPending: isUpgradePending,
    isSuccess,
    isError: isUpgradeError,
  } = musioApi.useMutation("post", "/user/recurring");

  useEffect(() => {
    if (!showProrate) return;
    getProrate({ body: { plan: target } });
  }, [showProrate, target, getProrate]);

  const upgradePlan = async () => {
    upgradeMutation({ body: { plan: target } });
    setRefresh(true);
  };

  const computed = useMemo(() => {
    const defaultTrigger =
      target === "annual" ? "Upgrade to Annual Billing" : "Update to Monthly Billing";
    const defaultTitle = target === "annual" ? "Upgrade to Annual Billing" : "Change to Monthly";
    const successMsg = target === "annual"
      ? "Your plan was upgraded to Annual."
      : "Your plan was switched to Monthly."
    return {
      triggerLabel: triggerLabel ?? defaultTrigger,
      title: title ?? defaultTitle,
      successMsg: successMsg,
    };
  }, [target, triggerLabel, title]);

  const hasNotice = isUpgradeError || isFetchError;
  const errorMessage = isUpgradeError ? "There was an error getting your prorated amount" : "There was an error updating your plan";

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button className={cn("w-full bg-musio-blue text-musio-light-lilac hover:text-musio-toggle", className)}>
          {computed.triggerLabel}
        </Button>
      </AlertDialogTrigger>

      <AlertDialogPortal>
        <AlertDialogOverlay className="AlertDialogOverlay" />
        <AlertDialogContent className="AlertDialogContent">
          <AlertDialogTitle className="AlertDialogTitle flex items-center justify-between gap-4 p-8 bg-musio-bg">
            <div className="text-[30px] font-semibold text-musio-light-lilac">{computed.title}</div>

            <AlertDialogCancel asChild>
              <Button
                type="button"
                className="select-none px-8 h-[38px] flex items-center justify-center rounded-full font-bold bg-musio-red text-musio-light-lilac cursor-pointer"
                disabled={isUpgradePending || isSuccess}
                onClick={() => (refresh ? window.location.reload() : null)}
              >
                <span className="text-sm">Go Back</span>
              </Button>
            </AlertDialogCancel>
          </AlertDialogTitle>

          <AlertDialogDescription className="p-8 bg-musio-bg space-y-4">
            {isUpgradePending ? (
              <div className="full centered">
                <LoadingIndicator />
              </div>
            ) : isSuccess ? (
              <NotificationRow intent="success">{computed.successMsg}</NotificationRow>
            ) : hasNotice ? (
              <NotificationRow intent="error">{errorMessage}</NotificationRow>
            ) : (
              <div className="full centered">
                {showProrate ? (
                  prorateData?.proration ? (
                    <p className="text-lg text-musio-light-lilac">
                      A charge of{" "}
                      <span className="font-bold">
                        {formatCurrency(
                          Number(prorateData.proration.amount),
                          prorateData.proration.currency!
                        )}
                      </span>{" "}
                      will be applied immediately
                    </p>
                  ) : isProratePending ? (
                    <LoadingIndicator />
                  ) : (
                    <p className="text-lg text-musio-light-lilac">
                      {confirmMessage ?? "Are you sure?"}
                    </p>
                  )
                ) : (
                  <p className="text-lg text-musio-light-lilac">
                    {confirmMessage ?? "Are you sure?"}
                  </p>
                )}
              </div>
            )}
          </AlertDialogDescription>

          <AlertDialogFooter className="p-8 flex items-center justify-center bg-musio-bg">
            {isUpgradePending ? null : isSuccess ? (
              <AlertDialogCancel asChild>
                <Button
                  onClick={() => (refresh ? window.location.reload() : null)}
                  className={cn(
                    "select-none w-[404px] h-[35px] flex items-center justify-center",
                    "rounded-2xl font-bold text-musio-mid-gray cursor-pointer",
                    "bg-musio-green"
                  )}
                >
                  Close
                </Button>
              </AlertDialogCancel>
            ) : (
              <Button
                disabled={isUpgradePending || (disableOnError && !!hasNotice)}
                onClick={upgradePlan}
                className={cn(
                  "select-none w-[404px] h-[35px] flex items-center justify-center",
                  "rounded-2xl font-bold text-musio-mid-gray cursor-pointer",
                  "bg-musio-green disabled:opacity-60 disabled:cursor-not-allowed"
                )}
              >
                Submit
              </Button>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogPortal>
    </AlertDialog>
  );
}

type NamedDialogProps = Omit<
  PlanChangeDialogProps,
  "target"
>;

export const MonthlyToAnnualDialog = (props: NamedDialogProps) => (
  <PlanChangeDialog target="annual" showProrate {...props} />
);

export const AnnualToMonthlyDialog = (props: NamedDialogProps) => (
  <PlanChangeDialog target="monthly" confirmMessage={"Are you sure?"} {...props} />
);

